import OidcClient from "./OidcClient";

/**
 * Wrapper around `fetch` that adds Authorization: Bearer &lt;accessToken&gt; and Accept-Language
 * derived from the SPA language switcher (localStorage <c>portalLang</c>).
 * Backend uses ASP.NET Core <c>UseRequestLocalization</c> + FluentValidation to localize
 * validation messages based on this header.
 *
 * @namespace computec.b2b.portal.auth
 */
export default class AuthFetch {
	private oidc: OidcClient;

	constructor(oidc: OidcClient) {
		this.oidc = oidc;
	}

	public async json<T = unknown>(input: RequestInfo, init: RequestInit = {}): Promise<T> {
		const r = await this.fetch(input, init);
		if (!r.ok) {
			throw new HttpError(r.status, await r.text());
		}
		if (r.status === 204) {
			return undefined;
		}
		return r.json() as Promise<T>;
	}

	public async fetch(input: RequestInfo, init: RequestInit = {}): Promise<Response> {
		const headers = new Headers(init.headers);
		const token = this.oidc.getAccessToken();
		if (token) {
			headers.set("Authorization", `Bearer ${token}`);
		}
		if (init.body && !headers.has("Content-Type")) {
			headers.set("Content-Type", "application/json");
		}
		if (!headers.has("Accept-Language")) {
			const lang = this.preferredLanguage();
			if (lang) {
				headers.set("Accept-Language", lang);
			}
		}
		const response = await fetch(input, { ...init, headers });
		// 401 = token expired lub sub stale (np. po reimporcie KC). Refresh schedule mógł nie
		// zdążyć zadziałać. Force interactive re-login — promise nigdy nie zwróci, następuje redirect.
		if (response.status === 401) {
			await this.oidc.forceRelogin();
		}
		return response;
	}

	private preferredLanguage(): string | null {
		try {
			const saved = window.localStorage?.getItem("portalLang");
			if (saved === "pl" || saved === "en") {
				return saved;
			}
		} catch {
			/* private mode lub brak localStorage */
		}
		return null;
	}
}

export class HttpError extends Error {
	public status: number;
	public bodyText: string;

	constructor(status: number, bodyText: string) {
		super(`HTTP ${status}`);
		this.status = status;
		this.bodyText = bodyText;
	}
}
